import { useState, useCallback, useEffect } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { usePagePermissions } from '../hooks/usePagePermissions';
import { useInventory } from '../hooks/useInventory';
import { useToast } from '../components/ui/Toast';
import { inventoryApi } from '../services/inventoryApi';
import { InventoryStats } from '../components/inventory/InventoryStats';
import { InventoryFilters } from '../components/inventory/InventoryFilters';
import { InventoryTable } from '../components/inventory/InventoryTable';
import { TransactionsModal } from '../components/inventory/TransactionsModal';
import { BuyersModal } from '../components/inventory/BuyersModal';
import { EmailLogModal } from '../components/inventory/EmailLogModal';
import { FlagConfirmModal } from '../components/inventory/FlagConfirmModal';
import { FlaggedItemsModal } from '../components/inventory/FlaggedItemsModal';
import { EmailComposerModal } from '../components/email/EmailComposerModal';
import { ArticleDetailDrawer } from '../components/inventory/ArticleDetailDrawer';
import type { FilterByValueAction } from '../components/inventory/InventoryRow';
import type { InventoryItem, InventoryBuyer, SimilarProductBuyers } from '../types/inventory';

const DRAWER_WIDTH_KEY = 'slowmover-drawer-width';
const DEFAULT_DRAWER_WIDTH = 70;

export function SlowNoMoverPage() {
    const { canEdit, isLoading: permLoading } = usePagePermissions('/slow-no-mover');
    const {
        items, total, loading, error, stats, flagStats,
        filters, filterOptions,
        sortBy, sortOrder, page, limit,
        setFilters, setSort, setPage, refresh,
    } = useInventory();
    const { toast } = useToast();

    const [drawerRefreshKey, setDrawerRefreshKey] = useState(0);

    const handleCommentAdded = useCallback(() => {
        refresh();
        setDrawerRefreshKey(k => k + 1);
    }, [refresh]);

    const handleFilterByValue = useCallback((action: FilterByValueAction) => {
        setFilters({ ...filters, [action.filterKey]: action.value });
    }, [filters, setFilters]);

    const [transactionItem, setTransactionItem] = useState<InventoryItem | null>(null);
    const [buyerItem, setBuyerItem] = useState<InventoryItem | null>(null);
    const [emailLogItem, setEmailLogItem] = useState<InventoryItem | null>(null);

    // Flag state
    const [flagModal, setFlagModal] = useState<{
        item: InventoryItem;
        flagType: 'ToBeDestroyed' | 'NewProduct';
        action: 'set' | 'remove';
    } | null>(null);
    const [flaggedItemsModal, setFlaggedItemsModal] = useState<'ToBeDestroyed' | 'NewProduct' | null>(null);

    // Email composer state
    const [emailItem, setEmailItem] = useState<InventoryItem | null>(null);
    const [emailBuyers, setEmailBuyers] = useState<InventoryBuyer[]>([]);
    const [emailSimilarProducts, setEmailSimilarProducts] = useState<SimilarProductBuyers[]>([]);
    const [emailLoading, setEmailLoading] = useState(false);

    // Drawer state (persisted width only, not drawer open state)
    const [drawerItemId, setDrawerItemId] = useState<string | null>(null);
    const [drawerWidth, setDrawerWidth] = useState<number>(() => {
        if (typeof window === 'undefined') return DEFAULT_DRAWER_WIDTH;
        const stored = window.localStorage.getItem(DRAWER_WIDTH_KEY);
        const parsed = stored ? Number(stored) : NaN;
        return Number.isFinite(parsed) && parsed >= 50 && parsed <= 85 ? parsed : DEFAULT_DRAWER_WIDTH;
    });

    useEffect(() => {
        if (typeof window !== 'undefined') {
            window.localStorage.setItem(DRAWER_WIDTH_KEY, String(Math.round(drawerWidth)));
        }
    }, [drawerWidth]);

    const drawerOpen = drawerItemId !== null;

    const handleSendEmail = useCallback(async (item: InventoryItem) => {
        setEmailItem(item);
        setEmailLoading(true);
        try {
            const buyersResp = await inventoryApi.getBuyers(item.ItemID);
            setEmailBuyers(buyersResp.buyers ?? []);
            setEmailSimilarProducts(buyersResp.similarProducts ?? []);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to load buyers', 'error');
            setEmailBuyers([]);
            setEmailSimilarProducts([]);
        } finally {
            setEmailLoading(false);
        }
    }, [toast]);

    const handleToggleFlag = useCallback((item: InventoryItem, flagType: 'ToBeDestroyed' | 'NewProduct') => {
        const isActive = flagType === 'ToBeDestroyed' ? item.IsToBeDestroyed : item.IsNewProduct;
        setFlagModal({ item, flagType, action: isActive ? 'remove' : 'set' });
    }, []);

    const handleFlagConfirm = useCallback(async (reason?: string) => {
        if (!flagModal) return;
        const { item, flagType, action } = flagModal;
        setFlagModal(null);
        try {
            if (action === 'set') {
                await inventoryApi.setFlag(item.ItemID, flagType, reason);
                toast('Flag set successfully', 'success');
            } else {
                await inventoryApi.removeFlag(item.ItemID, flagType, reason);
                toast('Flag removed successfully', 'success');
            }
            refresh();
            setDrawerRefreshKey(k => k + 1);
        } catch (err) {
            toast(err instanceof Error ? err.message : 'Failed to update flag', 'error');
        }
    }, [flagModal, refresh, toast]);

    if (permLoading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
            </div>
        );
    }

    const totalPages = Math.ceil(total / limit);

    return (
        <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900">Slow & No Mover</h1>
                    <p className="mt-1 text-sm text-gray-500">
                        Review and comment on slow-moving and non-moving inventory items (Piombi)
                    </p>
                </div>
                {canEdit && (
                    <span className="inline-flex items-center rounded-md bg-green-50 px-2 py-1 text-xs font-medium text-green-700 ring-1 ring-inset ring-green-600/20">
                        Edit Access
                    </span>
                )}
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-6">
                <InventoryStats
                    stats={stats}
                    flagStats={flagStats}
                    total={total}
                    onFlagTileClick={setFlaggedItemsModal}
                />
            </div>

            {/* Filters */}
            <InventoryFilters
                filters={filters}
                filterOptions={filterOptions}
                onFilterChange={setFilters}
            />

            {/* What the Betreuer filter removed. 480 of the 1'428 Piombi articles were never
                sold to anyone, so this filter hides a third of the stock — saying so is the
                difference between a filter and a bug report. */}
            {stats?.scope?.filtered && stats.scope.hiddenCount > 0 && (
                <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-2 text-sm text-amber-800">
                    {stats.scope.hiddenCount.toLocaleString('de-CH')} articles hidden by the sales-manager filter
                    {stats.scope.withoutAnyCustomerCount > 0 && (
                        <> — {stats.scope.withoutAnyCustomerCount.toLocaleString('de-CH')} of them have never been sold to any customer</>
                    )}
                    .
                </div>
            )}

            {/* Error */}
            {error && (
                <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                    {error}
                </div>
            )}

            {/* Table + Drawer split */}
            <div className="flex min-h-0 gap-3">
                <div
                    className="relative min-w-0"
                    style={{ width: drawerOpen ? `${100 - drawerWidth}%` : '100%' }}
                >
                    {loading && (
                        <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/60">
                            <div className="h-6 w-6 animate-spin rounded-full border-2 border-blue-600 border-t-transparent" />
                        </div>
                    )}
                    <InventoryTable
                        items={items}
                        sortBy={sortBy}
                        sortOrder={sortOrder}
                        onSort={setSort}
                        canEdit={canEdit}
                        onCommentAdded={handleCommentAdded}
                        onFilterByValue={handleFilterByValue}
                        onShowTransactions={setTransactionItem}
                        onToggleFlag={handleToggleFlag}
                        onShowBuyers={setBuyerItem}
                        onSendEmail={handleSendEmail}
                        onShowEmails={setEmailLogItem}
                        onShowDetails={setDrawerItemId}
                        compact={drawerOpen}
                        selectedItemId={drawerItemId}
                    />
                </div>
                {drawerOpen && drawerItemId && (
                    <ArticleDetailDrawer
                        itemId={drawerItemId}
                        onClose={() => setDrawerItemId(null)}
                        width={drawerWidth}
                        onWidthChange={setDrawerWidth}
                        canEdit={canEdit}
                        onToggleFlag={handleToggleFlag}
                        onCommentAdded={handleCommentAdded}
                        refreshKey={drawerRefreshKey}
                    />
                )}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-gray-200 pt-3">
                    <p className="text-sm text-gray-600">
                        Showing {((page - 1) * limit) + 1}–{Math.min(page * limit, total)} of {total}
                    </p>
                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => setPage(page - 1)}
                            disabled={page <= 1}
                            className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 px-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                            <ChevronLeft className="h-4 w-4" /> Prev
                        </button>
                        <span className="text-sm text-gray-600">
                            Page {page} of {totalPages}
                        </span>
                        <button
                            onClick={() => setPage(page + 1)}
                            disabled={page >= totalPages}
                            className="inline-flex h-8 items-center gap-1 rounded-md border border-gray-300 px-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                        >
                            Next <ChevronRight className="h-4 w-4" />
                        </button>
                    </div>
                </div>
            )}

            {/* Transactions Modal */}
            {transactionItem && (
                <TransactionsModal
                    isOpen={true}
                    onClose={() => setTransactionItem(null)}
                    item={transactionItem}
                />
            )}

            {/* Buyers Modal */}
            {buyerItem && (
                <BuyersModal
                    isOpen={true}
                    onClose={() => setBuyerItem(null)}
                    item={buyerItem}
                />
            )}

            {/* Email Log Modal (sent-email history for a single item) */}
            {emailLogItem && (
                <EmailLogModal
                    isOpen={true}
                    onClose={() => setEmailLogItem(null)}
                    item={emailLogItem}
                />
            )}

            {/* Flag Confirm Modal */}
            {flagModal && (
                <FlagConfirmModal
                    itemName={flagModal.item.ProductName}
                    flagType={flagModal.flagType}
                    action={flagModal.action}
                    onConfirm={handleFlagConfirm}
                    onCancel={() => setFlagModal(null)}
                />
            )}

            {/* Flagged Items Drill-Down Modal */}
            {flaggedItemsModal && (
                <FlaggedItemsModal
                    flagType={flaggedItemsModal}
                    canEdit={canEdit}
                    onClose={() => setFlaggedItemsModal(null)}
                    onFlagRemoved={refresh}
                />
            )}

            {/* Email Composer Modal (single item) */}
            {emailItem && !emailLoading && (
                <EmailComposerModal
                    isOpen={true}
                    context={{
                        type: 'SlowNoMoverItem',
                        item: emailItem,
                        buyers: emailBuyers,
                        similarProducts: emailSimilarProducts,
                    }}
                    onClose={() => {
                        setEmailItem(null);
                        setEmailBuyers([]);
                        setEmailSimilarProducts([]);
                    }}
                />
            )}
        </div>
    );
}
